import { getUserInfo } from './auth';

const MAX_NAVIGATION_ENTRIES = 50;
const MAX_EVENT_ENTRIES = 100;
const MAX_NETWORK_ENTRIES = 100;
const STORAGE_KEY = 'scpo-debug-session';

let state = {
  recording: false,
  startedAt: null,
  referrer: null,
  currentPage: null,
  userInfo: null,
  navigationLog: [],
  eventLog: [],
  networkLog: [],
};

const listeners = new Set();
let initialized = false;
let originalConsoleError = null;
let originalConsoleWarn = null;
let originalFetch = null;
let originalXhrOpen = null;
let originalXhrSend = null;

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

// useSyncExternalStore compare l'objet retourné par référence (Object.is) : chaque mise à jour
// doit produire un NOUVEL objet, jamais muter `state` en place, sinon React ne détecte aucun changement.
const setState = (changes) => {
  state = { ...state, ...changes };
  notify();
};

const persist = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ recording: state.recording, startedAt: state.startedAt })
    );
  } catch (_error) {
    // sessionStorage indisponible (navigation privée, quota...) : on continue sans persistance.
  }
};

const restore = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      state = { ...state, recording: Boolean(parsed.recording), startedAt: parsed.startedAt || null };
    }
  } catch (_error) {
    // Etat corrompu ou inaccessible : on repart d'un enregistrement inactif.
  }
};

const bounded = (array, entry, max) => {
  const next = [...array, entry];
  return next.length > max ? next.slice(next.length - max) : next;
};

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getSnapshot = () => state;

export const getServerSnapshot = () => ({
  recording: false,
  startedAt: null,
  referrer: null,
  currentPage: null,
  userInfo: null,
  navigationLog: [],
  eventLog: [],
  networkLog: [],
});

const captureEnvironment = () => ({
  userAgent: navigator.userAgent,
  language: navigator.language,
  online: navigator.onLine,
  viewport: { width: window.innerWidth, height: window.innerHeight },
  devicePixelRatio: window.devicePixelRatio,
});

// Cookie posé par le SDK Axeptio une fois le choix de consentement fait par l'utilisateur.
const captureConsent = () => {
  const match = document.cookie.match(/axeptio_authorized_vendors=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Garde les 2 premiers caractères visibles (assez pour qu'un collègue reconnaisse la personne
// qu'il soupçonne déjà), masque le reste : un inconnu tombant sur le JSON ne peut pas reconstituer
// l'identifiant complet.
const maskIdentifier = (value) => {
  if (!value) {
    return value;
  }
  const visible = value.slice(0, 2);
  return `${visible}${'*'.repeat(Math.max(value.length - visible.length, 3))}`;
};

const maskEmail = (email) => {
  if (!email || !email.includes('@')) {
    return maskIdentifier(email);
  }
  const [local, domain] = email.split('@');
  return `${maskIdentifier(local)}@${domain}`;
};

const refreshUserInfo = async () => {
  const result = await getUserInfo();
  const userInfo = result?.data
    ? {
        ...result.data,
        email: maskEmail(result.data.email),
        preferredUsername: maskIdentifier(result.data.preferredUsername),
        user: maskIdentifier(result.data.user),
      }
    : null;
  setState({ userInfo });
};

export const setCurrentPageData = ({ data, pageContext, path }) => {
  setState({ currentPage: { data, pageContext, path } });
};

export const pushNavigation = (path) => {
  if (!state.recording) {
    return;
  }
  refreshUserInfo();
  setState({ navigationLog: bounded(state.navigationLog, { path, timestamp: Date.now() }, MAX_NAVIGATION_ENTRIES) });
};

export const pushEvent = (level, message, stack) => {
  if (!state.recording) {
    return;
  }
  setState({ eventLog: bounded(state.eventLog, { level, message, stack, timestamp: Date.now() }, MAX_EVENT_ENTRIES) });
};

const pushNetworkFailure = (entry) => {
  if (!state.recording) {
    return;
  }
  setState({
    networkLog: bounded(state.networkLog, { ...entry, timestamp: Date.now() }, MAX_NETWORK_ENTRIES),
  });
};

export const startRecording = () => {
  setState({
    recording: true,
    startedAt: Date.now(),
    referrer: document.referrer || null,
    navigationLog: [],
    eventLog: [],
    networkLog: [],
  });
  persist();
};

const downloadJson = (payload) => {
  if (typeof window === 'undefined') {
    return;
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `debug-session-${Date.now()}.json`;
  link.click();
  URL.revokeObjectURL(url);
};

export const stopRecording = async () => {
  const stoppedAt = Date.now();
  await refreshUserInfo();

  downloadJson({
    meta: { startedAt: state.startedAt, stoppedAt, durationMs: stoppedAt - state.startedAt },
    environment: { ...captureEnvironment(), referrer: state.referrer },
    currentPage: state.currentPage,
    userInfo: state.userInfo,
    consent: captureConsent(),
    navigationLog: state.navigationLog,
    eventLog: state.eventLog,
    networkLog: state.networkLog,
  });

  setState({ recording: false, startedAt: null, navigationLog: [], eventLog: [], networkLog: [] });
  persist();
};

const isChord = (event, code) => event.ctrlKey && event.altKey && event.shiftKey && event.code === code;

const handleKeydown = (event) => {
  if (isChord(event, 'KeyD')) {
    startRecording();
  } else if (isChord(event, 'KeyS')) {
    stopRecording();
  }
};

const handleWindowError = (event) => {
  pushEvent('error', event.message, event.error?.stack);
};

const handleUnhandledRejection = (event) => {
  pushEvent('error', String(event.reason?.message || event.reason), event.reason?.stack);
};

const wrapFetch = () => {
  originalFetch = window.fetch?.bind(window);
  if (!originalFetch) {
    return;
  }
  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
    try {
      const response = await originalFetch(...args);
      if (!response.ok) {
        pushNetworkFailure({ url, method: args[1]?.method || 'GET', status: response.status });
      }
      return response;
    } catch (error) {
      pushNetworkFailure({ url, method: args[1]?.method || 'GET', status: 'network-error', message: error.message });
      throw error;
    }
  };
};

// axios utilise XMLHttpRequest par défaut dans le navigateur : sans ce wrapping, les appels
// CMS/Algolia/oauth2-proxy faits via axios échapperaient totalement à la capture réseau.
const wrapXhr = () => {
  originalXhrOpen = XMLHttpRequest.prototype.open;
  originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    this.__debugMethod = method;
    this.__debugUrl = url;
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function send(...args) {
    this.addEventListener('loadend', () => {
      if (this.status === 0 || this.status >= 400) {
        pushNetworkFailure({
          url: this.__debugUrl,
          method: this.__debugMethod,
          status: this.status || 'network-error',
        });
      }
    });
    return originalXhrSend.apply(this, args);
  };
};

export const initDebugCapture = () => {
  if (initialized || typeof window === 'undefined') {
    return;
  }
  initialized = true;

  restore();

  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  wrapFetch();
  wrapXhr();

  originalConsoleError = console.error.bind(console);
  originalConsoleWarn = console.warn.bind(console);

  console.error = (...args) => {
    originalConsoleError(...args);
    pushEvent('error', args.map(String).join(' '));
  };

  console.warn = (...args) => {
    originalConsoleWarn(...args);
    pushEvent('warn', args.map(String).join(' '));
  };
};
